/**
 * CONTRACT.md §2.10 — the allowance meter, and the allowance strip beside it.
 *
 * **The fill is neutral; only the part past the allowance is ochre**, because
 * only that part costs money. That is the whole of the colour decision, and it
 * is why `Meter` takes numbers rather than widths: a page that computed its own
 * percentages could put ochre on the wrong segment.
 *
 * The one sanctioned inline style. §4.1 allows `style="width:38%"` on a bar and
 * nothing else; `expectNoInlineStyle` in `src/test/contract.ts` allows exactly
 * `width` and `left`, which is what this file emits and nothing more.
 *
 * ## The scale
 *
 * The track spans `0 … scale`, where `scale = max(included, used, cap)` — the
 * whole story fits on the track, so a bar never runs off the end and a cap is
 * never invisible.
 *
 *   - `fill`  = `min(used, included)`               the part inside the allowance
 *   - `extra` = `used − included`, clamped to `0 … cap − included`
 *   - the cap marker is drawn only when `cap < scale`, which is exactly the case
 *     where something happened past the cap that the marker explains. With
 *     usage under the cap, `scale === cap` and a marker at the right-hand edge
 *     would say nothing.
 *
 * §2.10's last paragraph: there is **no** "projected to breach" state. A band at
 * 84% forecast to finish over is a neutral meter plus a `Notice need` carrying
 * the projection — `meter--over` would state something that is not yet true.
 */

import type { ReactNode } from 'react'

import { useFormat } from '../../format/useFormat.js'
import { cx, type Common } from './common.js'

/**
 * The whole a fraction is metered against, and the fraction as points of it.
 *
 * `Meter` takes a `used` and an `included`, never a width (CONTRACT.md §2.10), so a
 * ratio that is already a fraction of one — an allowance burn, an under-scoped
 * reading, anything a read model divided — is metered as "this many points of the
 * hundred it pays for". `analytics.html`'s own bar widths were drawn from exactly
 * that arithmetic: TuncLaw's 188% is a track that runs to 188 with the allowance at
 * 100, which is where its `width:53%` and `width:47%` come from.
 *
 * It lives here because it is about this component. Two pages had written it out —
 * `pages/studio/analytics/render.ts` and `pages/studio/clients/percent.ts`, each with
 * its own `100` beside its own name for it — and a third would have been the one to
 * get it wrong. It is **not** a formatter: the number goes into a bar, and
 * `format.fraction` is what turns the same ratio into something a person reads.
 */
export const WHOLE_IN_POINTS = 100

/** `0.801` becomes `80.1` — a fraction as points of `WHOLE_IN_POINTS`. */
export function percentagePoints(fraction: number): number {
  return fraction * WHOLE_IN_POINTS
}

export type MeterProps = Common & {
  /** What the allowance is for — "Content pieces", "SEO articles". */
  readonly label: ReactNode
  readonly used: number
  readonly included: number
  /** The hard ceiling past which usage is recorded and never billed. */
  readonly cap?: number | undefined
  /** `.meter--inline` — the 64px meter for a table cell or a burn indicator. */
  readonly inline?: boolean | undefined
  /** The `--fs-2` line under the track: "1 piece past allowance, billed at $95." */
  readonly foot?: ReactNode | undefined
  /** `6.0 / 6` rather than `6 / 6`, for an allowance measured in hours. */
  readonly fractionDigits?: number | undefined
  /**
   * What the top-right of the meter says, when `used / included` is not it.
   *
   * Two of `studio/*.html`'s meters are not counts: the ad-spend band is
   * `$4,180 / $5,000` — money, which `format.number` renders as `4,180` — and the
   * clients book's burn column is a bare `87%` with no reading at all beside it.
   * The **numbers stay the caller's** either way, because the geometry is what puts
   * ochre on the right segment; this changes only what is printed above it.
   *
   * `null` prints nothing, which is the book's column. Left out, it is the
   * `used / included` this component has always written.
   */
  readonly reading?: ReactNode | null | undefined
}

type Geometry = {
  readonly fill: string
  readonly extra: string
  readonly cap: string | null
}

/**
 * A percentage as CSS, at most two decimals — `80%`, `56.6%`, `43.4%`, the
 * spellings the mockups use. `toFixed` is arithmetic on a bar width rather than
 * on an amount, which is why it is not one of the rounding helpers `money.ts`
 * owns.
 */
function pct(part: number, scale: number): string {
  return `${String(Number(((part / scale) * 100).toFixed(2)))}%`
}

/** Null when the numbers are not numbers — the caller decides what that means. */
export function meterGeometry(used: number, included: number, cap?: number): Geometry | null {
  const finite = [used, included, ...(cap === undefined ? [] : [cap])]
  if (finite.some((value) => !Number.isFinite(value) || value < 0)) return null

  const scale = Math.max(included, used, cap ?? 0)
  if (scale === 0) return { fill: '0%', extra: '0%', cap: null }

  const ceiling = cap ?? Number.POSITIVE_INFINITY
  const fill = Math.min(used, included)
  const extra = Math.min(Math.max(used - included, 0), Math.max(ceiling - included, 0))

  return {
    fill: pct(fill, scale),
    extra: pct(extra, scale),
    cap: cap !== undefined && cap < scale ? pct(cap, scale) : null,
  }
}

export function Meter({
  label,
  used,
  included,
  cap,
  inline,
  foot,
  fractionDigits,
  reading,
  ...common
}: MeterProps) {
  const format = useFormat()
  const geometry = meterGeometry(used, included, cap)

  if (geometry === null) {
    /*
     * A NaN or a negative allowance is a bug upstream, and the two ways of
     * answering it are both worse on their own: throwing in production takes a
     * whole page down over a bar, and rendering silently hides the bug for ever.
     * So it throws where somebody is looking and draws an empty track where
     * nobody is.
     */
    if (import.meta.env.DEV) {
      throw new Error(
        `Meter needs finite, non-negative numbers: used=${String(used)}, included=${String(
          included,
        )}, cap=${String(cap)}.`,
      )
    }
    return <div className={cx('meter', inline && 'meter--inline')} {...common} />
  }

  /*
   * The decimal is on the *use* only — `6.0 / 6`, as `client/dashboard.html`
   * prints it. What is used is measured and can be a fraction of an hour; what
   * is included is a whole allowance somebody agreed to.
   */
  const digits = fractionDigits === undefined ? {} : { fractionDigits }
  const shown =
    reading === undefined ? `${format.number(used, digits)} / ${format.number(included)}` : reading

  return (
    <div
      className={cx('meter', used > included && 'meter--over', inline && 'meter--inline')}
      {...common}
    >
      <div className="meter__top">
        <span>{label}</span>
        {shown === null ? null : <span className="num">{shown}</span>}
      </div>
      <div className="meter__track">
        <span className="meter__fill" style={{ width: geometry.fill }} />
        <span className="meter__extra" style={{ width: geometry.extra }} />
        {geometry.cap === null ? null : (
          <span className="meter__cap" style={{ left: geometry.cap }} />
        )}
      </div>
      {foot === undefined ? null : <div className="meter__foot">{foot}</div>}
    </div>
  )
}

/* ------------------------------------------------------- the allowance strip */

export type AllowProps = Common & { readonly children: ReactNode }

/** One cell of the allowance strip. Hairline on the right, none on the last. */
export function Allow({ children, ...common }: AllowProps) {
  return (
    <div className="allow" {...common}>
      {children}
    </div>
  )
}

export type AllowTextProps = Common & { readonly children: ReactNode }

export function AllowName({ children, ...common }: AllowTextProps) {
  return (
    <div className="allow__name" {...common}>
      {children}
    </div>
  )
}

export function AllowQty({ children, ...common }: AllowTextProps) {
  return (
    <div className="allow__qty" {...common}>
      {children}
    </div>
  )
}

export function AllowOf({ children, ...common }: AllowTextProps) {
  return (
    <div className="allow__of" {...common}>
      {children}
    </div>
  )
}
